/**
 * HubOverviewPage — HUB-REDESIGN P2.
 *
 * Bento grid landing for hub.usetenon.com. Replaces the prior queue-led
 * default surface. Wires the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/overview-bento.jsx
 * to real endpoints (backend frozen for the redesign):
 *
 *   /hub/stats             — shipped_today, last ship sha/at, pulse
 *                             (migration_version, uptime_seconds), open_p0,
 *                             errors_today_open, errors_last_hour
 *   /hub/business/metrics  — MRR, ARR, plan breakdown, trial conversion
 *   /hub/business/history  — MRR series for the AreaChart
 *   /hub/tenants           — at-risk subset + lead-funnel breakdown
 *   /hub/queue             — P1/P2/P3 queue depth (P0 from /hub/stats)
 *   /hub/errors            — 24h histogram
 *   /hub/health/alerts     — active watchdog count
 *   /hub/health/rules      — full rule list for the 5x2 grid
 *
 * Design-side BACKEND_NEEDED tiles (Demos booked) render as `—` with the
 * BACKEND_NEEDED endpoint badge so the placement is preserved without a
 * fake number. Lead-funnel approximates via subscription_status counts
 * (Lead/Trial/Active/Past-due/Cancelled) until a CRM endpoint exists.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { formatTime } from '../utils/dateUtils'
import { formatCurrency } from '@/utils/formatCurrency'
import {
  Card, Endpoint, KPI, Sparkline, AreaChart, Bars, Donut, AlertChip, Icon,
  StatusDot, Pill,
} from './components'
import { IconButton } from '@/components/atoms/IconButton'
import {
  hubStats, hubBusiness, hubTenants, hubGithubIssues, hubErrors, hubHealth, hubCOO,
  hubMissionPipeline,
  type HubStats, type HubBusinessMetrics, type HubBusinessHistoryPoint,
  type HubTenantsResponse, type HubTenantSummary, type HubErrorEvent,
  type HubHealthAlert, type HubHealthRule, type HubCOOOverview,
  type HubDecisionRow, type HubMissionPipelineResponse,
} from './api'

// HUB-REPOINT (#1217): the workflow_items-era server rules (stuck_claim,
// stuck_preparing, stale_preparing, queued_stale_wip, session_age,
// manager_pulse_silent, coder_respawn_broken, in_progress_post_deploy_failed)
// were retired; the server now evaluates the GitHub-pipeline rules below. Kept
// in sync with WatchdogRules in internal/api/queue_watchdog_worker.go (and
// HubEngineeringPage.tsx's RULE_ORDER) so the panel never renders phantom
// rows for rules that no longer exist.
const RULE_ORDER = [
  'idle_queue', 'coordinator_silent', 'merge_stall', 'deploy_stall',
  'orphaned_tree', 'stale_stashes', 'auth_lapsed', 'context_warning',
  'watcher_events', 'working_tree',
]

export default function HubOverviewPage() {
  const [stats, setStats] = useState<HubStats | null>(null)
  const [metrics, setMetrics] = useState<HubBusinessMetrics | null>(null)
  const [history, setHistory] = useState<HubBusinessHistoryPoint[] | null>(null)
  const [tenants, setTenants] = useState<HubTenantsResponse | null>(null)
  const [queueByPrio, setQueueByPrio] = useState<{ p0: number; p1: number; p2: number; p3: number } | null>(null)
  const [pipeline, setPipeline] = useState<HubMissionPipelineResponse | null>(null)
  const [errors, setErrors] = useState<HubErrorEvent[]>([])
  const [healthAlerts, setHealthAlerts] = useState<HubHealthAlert[]>([])
  const [healthRules, setHealthRules] = useState<HubHealthRule[]>([])
  const [coo, setCoo] = useState<HubCOOOverview | null>(null)
  const [decisions, setDecisions] = useState<HubDecisionRow[]>([])
  const [historyDays, setHistoryDays] = useState<30 | 90 | 365>(90)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [s, m, h, t, queue, err, ha, hr, cooData, dec, pl] = await Promise.all([
          hubStats().catch(() => null),
          hubBusiness.metrics().catch(() => null),
          hubBusiness.history(historyDays).catch(() => null),
          hubTenants.list().catch(() => null),
          // HUB-REPOINT (#1217): queue depth from the live GitHub cache (open
          // issues), not the frozen workflow_items queue which reported a stale
          // 94-row backlog long after the 2026-05-24 cutover.
          hubGithubIssues.list({ status: 'queued' }).catch(() => null),
          hubErrors.list({ status: 'all', limit: 200 }).catch(() => null),
          hubHealth.alerts('active').catch(() => null),
          hubHealth.rules().catch(() => null),
          hubCOO.overview().catch(() => null),
          hubCOO.decisionsNeeded(3).catch(() => null),
          // HUB-ENG-NORMALIZE (#1358): the Deploy tile's coding readout comes from
          // the SAME pipeline endpoint as Mission Control / Engineering, so the
          // number can't disagree across pages.
          hubMissionPipeline().catch(() => null),
        ])
        if (cancelled) return
        setStats(s)
        setPipeline(pl)
        setMetrics(m)
        setHistory(h?.points ?? null)
        setTenants(t)
        const counts = { p0: 0, p1: 0, p2: 0, p3: 0 }
        for (const it of queue?.items ?? []) {
          if (it.priority_label === 'p0') counts.p0++
          else if (it.priority_label === 'p1') counts.p1++
          else if (it.priority_label === 'p2') counts.p2++
          else if (it.priority_label === 'p3') counts.p3++
        }
        setQueueByPrio(counts)
        setErrors(err?.errors ?? [])
        setHealthAlerts(ha?.alerts ?? [])
        setHealthRules(hr?.rules ?? [])
        setCoo(cooData)
        setDecisions(dec?.decisions ?? [])
        setRefreshedAt(new Date())
      } catch {
        // silent — individual catches above keep partial render alive
      }
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [historyDays])

  const mrrSeries = useMemo(
    () => (history ?? []).map(p => p.mrr_cents / 100),
    [history],
  )
  const errorsHourly = useMemo(() => bucketHourly(errors), [errors])

  const atRiskTenants = useMemo(() => pickAtRisk(tenants), [tenants])
  const leadFunnel = useMemo(() => buildLeadFunnel(tenants), [tenants])

  const planSegments = useMemo(() => {
    if (!metrics) return []
    return metrics.plan_breakdown.map((p, i) => ({
      label: p.plan,
      value: p.count,
      mrr_cents: p.mrr_cents,
      color: PLAN_COLORS[i % PLAN_COLORS.length],
    }))
  }, [metrics])

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <AlertsStrip stats={stats} healthAlerts={healthAlerts} atRiskCount={tenants?.summary.at_risk_count ?? 0} />

      <div className="hub-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 10 }}>
        <MRRTile metrics={metrics} mrrSeries={mrrSeries} historyDays={historyDays} setHistoryDays={setHistoryDays} />
        <KPICluster metrics={metrics} tenantsSummary={tenants?.summary} />
        <PlanMixTile segments={planSegments} totalCount={tenants?.summary.total ?? 0} />

        <LeadFunnelTile funnel={leadFunnel} />
        <AtRiskTile tenants={atRiskTenants} />
        <ShipsTile stats={stats} />

        <ErrorsTile errors={errors} hourly={errorsHourly} />
        <QueueDepthTile
          p0={queueByPrio?.p0 ?? stats?.open_p0 ?? 0}
          p1={queueByPrio?.p1 ?? 0}
          p2={queueByPrio?.p2 ?? 0}
          p3={queueByPrio?.p3 ?? 0}
        />
        <WatchdogTile alerts={healthAlerts} rules={healthRules} />

        <DeployTile stats={stats} pipeline={pipeline} />
        <COODigestTile coo={coo} />
        <DecisionsNeededTile decisions={decisions} />
      </div>

      {refreshedAt && (
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-dim)' }} className="mono">
          refreshed {formatTime(refreshedAt)}
        </div>
      )}
    </div>
  )
}

// ── Tile: alerts strip ──────────────────────────────────────────────────
function AlertsStrip({
  stats, healthAlerts, atRiskCount,
}: {
  stats: HubStats | null
  healthAlerts: HubHealthAlert[]
  atRiskCount: number
}) {
  const chips: { tone: 'err' | 'warn' | 'info'; label: string; time?: string }[] = []
  if ((stats?.open_p0 ?? 0) > 0) {
    chips.push({ tone: 'err', label: `${stats!.open_p0} open P0`, time: 'now' })
  }
  for (const a of healthAlerts.slice(0, 2)) {
    chips.push({
      tone: (a.severity === 'critical' ? 'err' : a.severity === 'warn' ? 'warn' : 'info'),
      label: a.title || a.rule_name,
      time: relTime(a.last_seen_at),
    })
  }
  if (atRiskCount > 0) {
    chips.push({ tone: 'warn', label: `${atRiskCount} tenants at-risk`, time: 'now' })
  }
  if ((stats?.errors_last_hour ?? 0) > 0) {
    chips.push({ tone: 'err', label: `${stats!.errors_last_hour} errors / 1h`, time: 'now' })
  }

  const total = chips.length
  return (
    <Card>
      <div className="row" style={{ padding: '8px 12px', gap: 10, alignItems: 'center', borderTop: 0 }}>
        <Pill variant={total > 0 ? 'err' : 'ok'}>
          <Icon name="alert-triangle" size={11} style={{ marginRight: 4 }} />
          {total > 0 ? `${total} attention` : 'all clear'}
        </Pill>
        <span style={{ flex: 1, display: 'flex', gap: 6, overflow: 'hidden', flexWrap: 'wrap' }}>
          {chips.map((c, i) => (
            <AlertChip key={i} tone={c.tone}>
              {c.label}
              {c.time && <span className="mono" style={{ opacity: 0.6, marginLeft: 6 }}>{c.time}</span>}
            </AlertChip>
          ))}
        </span>
      </div>
    </Card>
  )
}

// ── Tile: MRR ─────────────────────────────────────────────────────────
function MRRTile({
  metrics, mrrSeries, historyDays, setHistoryDays,
}: {
  metrics: HubBusinessMetrics | null
  mrrSeries: number[]
  historyDays: 30 | 90 | 365
  setHistoryDays: (d: 30 | 90 | 365) => void
}) {
  const mrrUsd = (metrics?.mrr_cents ?? 0) / 100
  const arrUsd = (metrics?.arr_cents ?? 0) / 100
  const netNew = (metrics?.net_new_mrr_cents ?? 0) / 100
  const ranges: Array<{ label: string; days: 30 | 90 | 365 }> = [
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
    { label: '12m', days: 365 },
  ]
  return (
    <Card
      icon="trending-up" title="Business · MRR" endpoint="/hub/business/metrics"
      style={{ gridColumn: 'span 5' }}
    >
      <div className="row" style={{ alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        <span className="mono" style={{ fontSize: 28, fontWeight: 500 }}>
          {fmtUsd(mrrUsd)}
        </span>
        <span className={`delta ${netNew >= 0 ? 'up' : 'down'} mono`}>
          {netNew >= 0 ? '+' : ''}{fmtUsd(netNew)} MTD
        </span>
        <span className="delta flat mono">ARR {fmtUsd(arrUsd)}</span>
        <span className="grow" />
        <div className="row" style={{ gap: 4 }}>
          {ranges.map(r => (
            <button
              key={r.label}
              type="button"
              onClick={() => setHistoryDays(r.days)}
              className="pill ghost"
              style={{
                background: historyDays === r.days ? 'var(--surface-3)' : 'transparent',
                cursor: 'pointer', border: 0,
                color: historyDays === r.days ? 'var(--text)' : 'var(--text-muted)',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {mrrSeries.length > 1 ? (
        <AreaChart data={mrrSeries} color="var(--accent)" height={130} />
      ) : (
        <EmptyChart label="No MRR history yet" />
      )}
    </Card>
  )
}

// ── Tile: KPI cluster ──────────────────────────────────────────────────
function KPICluster({
  metrics, tenantsSummary,
}: {
  metrics: HubBusinessMetrics | null
  tenantsSummary?: HubTenantsResponse['summary']
}) {
  const active = tenantsSummary?.active ?? 0
  const trialing = tenantsSummary?.trialing ?? 0
  const atRisk = tenantsSummary?.at_risk_count ?? 0
  const trialConvPct = metrics?.trial_conversion_rate_pct ?? 0
  const trialStarts = metrics?.trial_starts_mtd ?? 0
  const trialConv = metrics?.trial_conversions_mtd ?? 0
  return (
    <Card style={{ gridColumn: 'span 4', padding: 0 }} bodyPad={false}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
        <KPI label="Active tenants" value={active} delta={`${trialing} trialing`} tone="up" endpoint="/hub/tenants" />
        <div style={{ borderLeft: '1px solid var(--border)' }}>
          <KPI label="Trial → Paid" value={`${Math.round(trialConvPct)}%`} delta={`MTD ${trialConv}/${trialStarts}`} tone={trialConvPct >= 20 ? 'up' : 'flat'} endpoint="/hub/business/metrics" />
        </div>
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <KPI label="Demos booked" value="—" delta="this week" tone="flat" endpoint="BACKEND_NEEDED" />
        </div>
        <div style={{ borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)' }}>
          <KPI label="At-risk" value={atRisk} delta={`of ${tenantsSummary?.total ?? 0}`} tone={atRisk > 0 ? 'down' : 'flat'} endpoint="/hub/tenants" />
        </div>
      </div>
    </Card>
  )
}

// ── Tile: Plan mix donut ────────────────────────────────────────────────
const PLAN_COLORS = [
  'var(--accent)',
  'var(--info)',
  'var(--ok)',
  'var(--warn)',
  'var(--text-faint)',
]

function PlanMixTile({
  segments, totalCount,
}: {
  segments: { label: string; value: number; mrr_cents: number; color: string }[]
  totalCount: number
}) {
  const inactive = Math.max(0, totalCount - segments.reduce((a, s) => a + s.value, 0))
  const fullSegments = inactive > 0
    ? [...segments, { label: 'Inactive', value: inactive, mrr_cents: 0, color: 'var(--text-faint)' }]
    : segments
  const drawSegments = fullSegments.length > 0
    ? fullSegments.map(s => ({ value: Math.max(s.value, 0.001), color: s.color }))
    : [{ value: 1, color: 'var(--text-faint)' }]
  return (
    <Card title="Plan mix" endpoint="/hub/business/metrics" style={{ gridColumn: 'span 3' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Donut size={84} segments={drawSegments} />
        <div style={{ flex: 1, fontSize: 11.5, minWidth: 0 }}>
          {fullSegments.length === 0 && (
            <span style={{ color: 'var(--text-dim)' }}>No plan data</span>
          )}
          {fullSegments.map((s, i) => (
            <div key={i} className="row" style={{ justifyContent: 'space-between', padding: '3px 0' }}>
              <span className="row" style={{ gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span className="dot" style={{ background: s.color }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label || '—'}</span>
              </span>
              <span className="mono">{s.value} · {fmtUsd(s.mrr_cents / 100)}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}

// ── Tile: Lead funnel ──────────────────────────────────────────────────
type FunnelRow = { label: string; value: number }

function buildLeadFunnel(t: HubTenantsResponse | null): FunnelRow[] {
  if (!t) return []
  const s = t.summary
  return [
    { label: 'Lead', value: s.total },
    { label: 'Trial', value: s.trialing },
    { label: 'Active', value: s.active },
    { label: 'PQL', value: s.past_due },
    { label: 'Won', value: Math.max(0, s.active - s.past_due) },
  ]
}

function LeadFunnelTile({ funnel }: { funnel: FunnelRow[] }) {
  const max = funnel[0]?.value || 1
  return (
    <Card
      icon="dollar-sign" title="Sales · Lead funnel · 30d" endpoint="/hub/tenants"
      style={{ gridColumn: 'span 4' }}
    >
      {funnel.length === 0 && <span style={{ color: 'var(--text-dim)' }}>No tenant data</span>}
      {funnel.map((row, i) => {
        const pct = max ? (row.value / max) * 100 : 0
        return (
          <div key={i} className="row" style={{ gap: 8, padding: '3px 0' }}>
            <span style={{ width: 50, fontSize: 11.5, color: 'var(--text-muted)' }}>{row.label}</span>
            <div style={{ flex: 1, height: 14, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', opacity: 0.85 }} />
            </div>
            <span className="mono" style={{ fontSize: 11.5, width: 28, textAlign: 'right' }}>{row.value}</span>
          </div>
        )
      })}
    </Card>
  )
}

// ── Tile: at-risk tenants ──────────────────────────────────────────────
function pickAtRisk(t: HubTenantsResponse | null): HubTenantSummary[] {
  if (!t) return []
  return t.tenants
    .filter(x => x.subscription_status === 'past_due' || (x.flags && x.flags.length > 0))
    .slice(0, 4)
}

function severityFor(t: HubTenantSummary): 'err' | 'warn' | 'info' {
  if (t.subscription_status === 'past_due') return 'err'
  if ((t.flags || []).some(f => f.includes('EXPIRED') || f.includes('FAILED'))) return 'err'
  return 'warn'
}

function reasonFor(t: HubTenantSummary): string {
  if (t.subscription_status === 'past_due') return 'Past due'
  if (t.subscription_status === 'cancelled') return 'Cancelled'
  if ((t.flags || []).length) return t.flags.slice(0, 2).join(' · ')
  return t.subscription_status
}

function AtRiskTile({ tenants }: { tenants: HubTenantSummary[] }) {
  return (
    <Card
      icon="users" title="Customers · At-risk tenants" endpoint="/hub/tenants"
      style={{ gridColumn: 'span 5' }}
      right={
        <a className="btn ghost" href="/customers" style={{ fontSize: 11, textDecoration: 'none' }}>
          Customers <Icon name="arrow-right" size={10} />
        </a>
      }
    >
      {tenants.length === 0 && (
        <div style={{ padding: '8px 0', color: 'var(--text-dim)', fontSize: 12 }}>
          All clear — no at-risk tenants right now.
        </div>
      )}
      {tenants.map((t, i) => (
        <div
          key={t.company_id}
          className="row"
          style={{
            padding: '7px 0',
            borderBottom: i < tenants.length - 1 ? '1px solid var(--border)' : 0,
            gap: 10,
          }}
        >
          <StatusDot tone={severityFor(t)} />
          <a
            href={`/tenants/${t.company_id}`}
            style={{
              fontSize: 12.5, color: 'var(--text)',
              flex: '0 0 200px', overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', textDecoration: 'none',
            }}
          >
            {t.company_name}
          </a>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {reasonFor(t)}
          </span>
          <IconButton
            href={`/tenants/${t.company_id}`}
            aria-label={`Open ${t.company_name}`}
            size="sm"
            variant="ghost"
          >
            <Icon name="arrow-up-right" size={11} />
          </IconButton>
        </div>
      ))}
    </Card>
  )
}

// ── Tile: today's ships ────────────────────────────────────────────────
function ShipsTile({ stats }: { stats: HubStats | null }) {
  const today = stats?.shipped_today ?? 0
  const yest = stats?.shipped_yesterday ?? 0
  // Use real 24-day history from the server; fall back to zeroes while loading.
  const series = useMemo(
    () => stats?.ships_history ?? new Array(24).fill(0),
    [stats?.ships_history]
  )
  const delta = today - yest
  return (
    <Card icon="package" title="Today's ships" endpoint="/hub/queue?status=done" style={{ gridColumn: 'span 3' }}>
      <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
        <span className="mono" style={{ fontSize: 24, fontWeight: 500 }}>{today}</span>
        <span className={`delta ${delta >= 0 ? 'up' : 'down'} mono`}>
          vs {yest} yest.
        </span>
      </div>
      <div style={{ marginTop: 6 }}>
        <Sparkline data={series} color="var(--accent)" height={36} />
      </div>
    </Card>
  )
}

// ── Tile: errors 24h ───────────────────────────────────────────────────
function ErrorsTile({ errors, hourly }: { errors: HubErrorEvent[]; hourly: number[] }) {
  const total = errors.length
  const lastHour = hourly[hourly.length - 1] ?? 0
  return (
    <Card
      icon="alert-triangle" title="Errors · 24h" endpoint="/hub/errors"
      style={{ gridColumn: 'span 4' }}
    >
      <div className="row" style={{ alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span className="mono" style={{ fontSize: 24, fontWeight: 500, color: total > 0 ? 'var(--err)' : 'var(--text)' }}>
          {total}
        </span>
        <span className="delta flat mono">today · {lastHour}/h current</span>
      </div>
      <Bars data={hourly} color="var(--err)" height={48} />
      <div className="row mono" style={{ justifyContent: 'space-between', fontSize: 9.5, color: 'var(--text-dim)', marginTop: 2 }}>
        <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>now</span>
      </div>
    </Card>
  )
}

// ── Tile: queue depth ──────────────────────────────────────────────────
function QueueDepthTile({ p0, p1, p2, p3 }: { p0: number; p1: number; p2: number; p3: number }) {
  const items = [
    { k: 'P0', v: p0, tone: 'err' as const },
    { k: 'P1', v: p1, tone: 'warn' as const },
    { k: 'P2', v: p2, tone: 'info' as const },
    { k: 'P3', v: p3, tone: 'dim' as const },
  ]
  return (
    <Card icon="list" title="Queue depth" endpoint="/hub/queue" style={{ gridColumn: 'span 5' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {items.map(b => (
          <div key={b.k} style={{ background: 'var(--surface-2)', borderRadius: 6, padding: '8px 10px' }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>{b.k}</span>
              <StatusDot tone={b.tone} />
            </div>
            <div className="mono" style={{ fontSize: 18, marginTop: 2 }}>{b.v}</div>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ── Tile: watchdog rules ───────────────────────────────────────────────
function WatchdogTile({ alerts, rules }: { alerts: HubHealthAlert[]; rules: HubHealthRule[] }) {
  // Build a stable rule list: known order first, then any new ones from API.
  const known = new Set(RULE_ORDER)
  const extras = rules.map(r => r.name).filter(n => !known.has(n))
  const ordered = [...RULE_ORDER, ...extras].slice(0, 10)

  const alertByRule = useMemo(() => {
    const m = new Map<string, HubHealthAlert>()
    for (const a of alerts) m.set(a.rule_name, a)
    return m
  }, [alerts])

  const activeCount = alerts.length
  return (
    <Card
      icon="shield" title={`Health · ${ordered.length} watchdog rules`} endpoint="/hub/health/rules"
      style={{ gridColumn: 'span 7' }}
      right={activeCount > 0 ? <Pill variant="warn">{activeCount} active</Pill> : <Pill variant="ok">all ok</Pill>}
      bodyPad={false}
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1,
        background: 'var(--border)', borderBottomLeftRadius: 9, borderBottomRightRadius: 9, overflow: 'hidden',
      }}>
        {ordered.map(name => {
          const alert = alertByRule.get(name)
          const tone: 'ok' | 'warn' | 'err' = !alert
            ? 'ok'
            : alert.severity === 'critical' ? 'err' : 'warn'
          return (
            <div key={name} style={{ background: 'var(--surface)', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <StatusDot tone={tone} />
                {alert && (
                  <span className="mono" style={{ fontSize: 10, color: tone === 'err' ? 'var(--err)' : 'var(--warn)' }}>
                    {relTime(alert.last_seen_at)}
                  </span>
                )}
              </div>
              <span className="mono" style={{ fontSize: 10.5, color: alert ? 'var(--text)' : 'var(--text-muted)' }}>
                {name}
              </span>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// ── Tile: deploy ──────────────────────────────────────────────────────
function DeployTile({ stats, pipeline }: { stats: HubStats | null; pipeline: HubMissionPipelineResponse | null }) {
  const pulse = stats?.pulse
  const lastShipSha = stats?.last_ship_sha?.slice(0, 7) ?? '—'
  const lastShipAgo = stats?.last_ship_at ? relTime(stats.last_ship_at) : '—'
  const uptime = pulse ? formatUptime(pulse.uptime_seconds) : '—'
  // HUB-ENG-NORMALIZE (#1358): "coding" reads the shared pipeline endpoint's
  // Coding stage (status:in-progress minus review-waiting) — the SAME source
  // and name Mission Control + Engineering use — replacing the raw
  // stats.active_claims "sessions" count that disagreed across pages.
  const coding = pipeline?.stages.find(s => s.key === 'coding')?.count ?? null
  return (
    <Card
      icon="git-branch" title="Deploy" endpoint="/hub/stats"
      style={{ gridColumn: 'span 5' }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 11.5 }}>
        <DeployCell label="Migration" value={pulse?.migration_version ?? '—'} sub={pulse?.app_env || ''} />
        <DeployCell label="Last ship" mono value={lastShipSha} sub={lastShipAgo} />
        <DeployCell label="Uptime" value={uptime} sub={coding != null ? `${coding} coding` : '— coding'} />
      </div>
    </Card>
  )
}

function DeployCell({ label, value, sub, mono }: { label: string; value: React.ReactNode; sub: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>{label}</div>
      <div className={mono ? 'mono' : ''} style={{ fontSize: 16, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ color: 'var(--text-dim)', marginTop: 1 }}>{sub}</div>}
    </div>
  )
}

// ── Tile: COO digest (BUSINESS-AUTONOMY Phase 8b) ─────────────────────
function COODigestTile({ coo }: { coo: HubCOOOverview | null }) {
  const digests = coo?.digests ?? []
  const agg = coo?.aggregates
  return (
    <Card
      icon="briefcase" title="COO weekly digest" endpoint="/hub/coo/overview"
      style={{ gridColumn: 'span 7' }}
    >
      {digests.length === 0 ? (
        <EmptyChart label="No COO digest yet — coordinator paused" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: digests.length > 1 ? '1fr 1fr' : '1fr', gap: 10 }}>
          {digests.map(d => (
            <div
              key={d.agent_id}
              style={{
                padding: 10, borderRadius: 8,
                background: 'var(--surface-2, rgba(255,255,255,0.02))',
                border: '1px solid var(--border, rgba(255,255,255,0.06))',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}
            >
              <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                <Pill variant="info">{d.product}</Pill>
                <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>{d.week_tag}</span>
              </div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{d.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
                {d.body || 'Placeholder digest — body fills in once writers ship.'}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{
        marginTop: 10, paddingTop: 8,
        borderTop: '1px solid var(--border, rgba(255,255,255,0.06))',
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
        fontSize: 11.5,
      }}>
        <DeployCell label="Touchpoints 7d" value={agg?.touchpoints_last_7d ?? '—'} sub="agent_audit_log" />
        <DeployCell label="Agent spend" mono value={formatCurrency(agg?.agent_spend_usd_total ?? 0, { decimals: 2 })} sub="all-time" />
        <DeployCell label="Milestones" value={agg?.upcoming_milestones ?? '—'} sub="upcoming" />
        <DeployCell label="Awaiting Joe" value={agg?.escalations_to_joe ?? '—'} sub="in_review" />
      </div>
    </Card>
  )
}

// ── Tile: 3 decisions needed (BUSINESS-AUTONOMY Phase 8b) ─────────────
function DecisionsNeededTile({ decisions }: { decisions: HubDecisionRow[] }) {
  return (
    <Card
      icon="alert-triangle" title="Decisions needed" endpoint="/hub/decisions-needed"
      style={{ gridColumn: 'span 5' }}
    >
      {decisions.length === 0 ? (
        <EmptyChart label="No escalations awaiting Joe — all clear." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {decisions.map(d => (
            <div
              key={d.id}
              style={{
                padding: 10, borderRadius: 8,
                background: 'var(--surface-2, rgba(255,255,255,0.02))',
                border: '1px solid var(--border, rgba(255,255,255,0.06))',
              }}
            >
              <div className="row" style={{ alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Pill variant={d.priority === 'p0' ? 'err' : d.priority === 'p1' ? 'warn' : 'info'}>{d.priority}</Pill>
                <Pill variant="ghost">{d.product}</Pill>
                <Pill variant="ghost">{d.department}</Pill>
                <Pill variant="ghost">{d.type}</Pill>
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-dim)' }}>
                  {relTime(d.updated_at)}
                </span>
              </div>
              <div style={{ marginTop: 4, fontSize: 13, fontWeight: 500 }}>{d.title}</div>
              {d.row_id && (
                <div className="mono" style={{ marginTop: 2, fontSize: 11, color: 'var(--text-dim)' }}>
                  {d.row_id}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// ── helpers ───────────────────────────────────────────────────────────
function fmtUsd(n: number): string {
  if (!isFinite(n)) return '—'
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${Math.round(n).toLocaleString()}`
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0 || !isFinite(ms)) return '—'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.floor(hr / 24)
  return `${day}d`
}

function formatUptime(secs: number): string {
  if (!secs || secs < 0) return '—'
  const day = Math.floor(secs / 86400)
  const hr = Math.floor((secs % 86400) / 3600)
  const min = Math.floor((secs % 3600) / 60)
  if (day > 0) return `${day}d ${hr}h`
  if (hr > 0) return `${hr}h ${min}m`
  if (min > 0) return `${min}m`
  return `${secs}s`
}

function bucketHourly(errors: HubErrorEvent[]): number[] {
  const buckets = new Array(24).fill(0)
  const now = Date.now()
  for (const e of errors) {
    const ageMs = now - new Date(e.created_at).getTime()
    const ageHr = Math.floor(ageMs / (3600 * 1000))
    if (ageHr < 0 || ageHr >= 24) continue
    buckets[23 - ageHr]++
  }
  return buckets
}


function EmptyChart({ label }: { label: string }) {
  return (
    <div style={{
      height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-dim)', fontSize: 11, border: '1px dashed var(--border)', borderRadius: 6,
    }}>
      {label}
    </div>
  )
}
